import {
  LoaderCircle,
  TriangleAlert,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  formatearKilosCompra,
  formatearMontoCompra,
  nombreProveedorCompra,
} from '../../utils/compras'
import { formatearFechaPos } from '../../utils/pos'

function Dato({
  etiqueta,
  valor,
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">
        {valor || '—'}
      </p>
    </div>
  )
}

function AnularCompraModal({
  abierto,
  compra,
  procesando = false,
  onCancelar,
  onConfirmar,
}) {
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (abierto) {
      setMotivo('')
      setError('')
    }
  }, [abierto])

  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape' && !procesando) {
        onCancelar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, procesando, onCancelar])

  if (!abierto || !compra) return null

  const alHacerClicFuera = (event) => {
    if (procesando) return

    if (event.target === event.currentTarget) {
      onCancelar?.()
    }
  }

  const confirmar = () => {
    const motivoLimpio = motivo.trim()

    if (!motivoLimpio) {
      setError('Debe indicar el motivo de anulación.')
      return
    }

    setError('')
    onConfirmar(motivoLimpio)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="anular-compra-titulo"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <TriangleAlert size={22} />
            </div>

            <h2
              id="anular-compra-titulo"
              className="text-lg font-bold text-slate-900"
            >
              Anular compra
            </h2>
          </div>

          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={22} />
          </button>
        </div>

        <p className="mt-4 text-sm text-slate-600">
          La compra quedará anulada y en solo lectura. No se elimina: se
          conserva para auditoría.
        </p>

        <div className="mt-5 grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <Dato
            etiqueta="N° compra"
            valor={compra.codigo_compra}
          />

          <Dato
            etiqueta="Fecha"
            valor={formatearFechaPos(compra.fecha_compra)}
          />

          <Dato
            etiqueta="Proveedor"
            valor={nombreProveedorCompra(compra)}
          />

          <Dato
            etiqueta="Kg netos"
            valor={formatearKilosCompra(compra.peso_neto_kg)}
          />

          <Dato
            etiqueta="Total"
            valor={formatearMontoCompra(compra.total_compra)}
          />
        </div>

        <div className="mt-5">
          <label
            htmlFor="anular-compra-motivo"
            className="mb-2 block text-sm font-semibold text-slate-700"
          >
            Motivo de anulación
            <span className="ml-1 text-red-500">*</span>
          </label>

          <textarea
            id="anular-compra-motivo"
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Ejemplo: Compra ingresada por error"
            disabled={procesando}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
          />

          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            className="min-h-12 w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmar}
            disabled={procesando}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-3 font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-400 sm:w-auto"
          >
            {procesando && (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            )}
            {procesando ? 'Anulando...' : 'Anular compra'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AnularCompraModal
